// Making an app for the M5Stack Tab5 by talking to an agent (docs/make.md),
// in the room.
//
// Two places. In the room itself, a box at the foot of the window: the first
// thing to do here is say what app to make, and under it the account's apps
// to open again. Once an app is being made, the workspace (./tab5.ts): the
// conversation on the left and the app on a screen beside it.

import { makeInfo, tab5Games } from "../api/make.ts";
import { track } from "../api/events.ts";
import type { StudioCore, StudioState } from "../core/studio.ts";
import { CREDITS, MAKE_PREVIEW_KEY, MESSAGE_MAX, type CreditsInfo, type MakeInfo } from "../core/make.ts";
import { h, svg } from "./dom.ts";
import { FACE, ICON, prompter } from "./make-parts.ts";
import { t } from "./lang.ts";
import { Tab5View } from "./tab5.ts";

/** What the maker's views need from the page around them. */
export interface MakeHost {
  core(): StudioCore | null;
  toast(message: string): void;
  /** Asks the visitor to sign in with an account; `prompt` is kept through it. */
  signIn(prompt?: string): void;
}

/** The message a visitor wrote before a sign-in took them away, kept in the tab. */
const KEPT_PROMPT = "pocket.make.prompt";

export class MakeView {
  private info: MakeInfo | null = null;
  private dock: HTMLElement;
  private dockField: HTMLTextAreaElement;
  private dockCredits: HTMLElement;
  /** The account's newest apps under the box, to open again. */
  private mine: HTMLElement;
  private tab5: Tab5View;

  constructor(private root: HTMLElement, private host: MakeHost) {
    // The box at the foot of the room.
    const send = h("button", { class: "make-send", type: "button", "aria-label": t("make.dock.send"), onclick: () => this.fromDock() },
      h("span", { text: t("make.dock.send") }), svg(ICON.send));
    this.dockField = prompter(t("make.tab5.example"), () => this.fromDock(), 4);
    this.dockField.setAttribute("aria-label", t("make.tab5.on"));
    this.dockField.addEventListener("input", () => this.dock.classList.toggle("typed", this.dockField.value.trim() !== ""));
    this.dockCredits = h("span", { class: "make-credits" });
    this.mine = h("div", { class: "make-ways make-mine" });
    this.mine.hidden = true;
    this.dock = h("section", { class: "make-dock tab5", "aria-label": t("make.dock.label") },
      h("div", { class: "make-box" }, h("span", { class: "make-face" }, svg(FACE)), this.dockField, send),
      h("div", { class: "make-ways" }, h("span", { class: "make-for", text: t("make.dock.for") }), this.dockCredits),
      this.mine);
    this.dock.hidden = true;

    // The workspace.
    this.tab5 = new Tab5View({
      core: () => this.host.core(),
      toast: (message) => this.host.toast(message),
      credits: () => this.credits,
      setCredits: (credits) => this.setCredits(credits),
    });
    root.append(this.dock, this.tab5.space);
  }

  /** Asks the server whether the maker is open here, and turns the views on when it is and this browser may see it. */
  async start(): Promise<boolean> {
    const query = new URLSearchParams(location.search);
    const flag = query.get("make");
    if (flag === "off") localStorage.removeItem(MAKE_PREVIEW_KEY);
    else if (flag !== null) localStorage.setItem(MAKE_PREVIEW_KEY, "1");
    // What the landing page's box was given waits in this one: the visitor presses once more, here.
    const idea = query.get("idea")?.trim().slice(0, MESSAGE_MAX) ?? "";
    if (query.has("idea")) {
      const url = new URL(location.href);
      url.searchParams.delete("idea");
      history.replaceState(history.state, "", url);
    }
    const info = await makeInfo();
    if (!info || !info.tab5 || info.mode === "off" || (info.mode === "preview" && localStorage.getItem(MAKE_PREVIEW_KEY) !== "1")) return false;
    this.info = info;
    this.root.dataset.make = "on";
    if (idea) {
      this.dockField.value = idea;
      this.dock.classList.add("typed");
    }
    this.showCredits();
    if (this.signedIn()) void this.showMine();
    return true;
  }

  /** The message the tab kept through a sign-in, taken out of it: the room sends it once it is open. */
  takePrompt(): string | null {
    const kept = sessionStorage.getItem(KEPT_PROMPT);
    sessionStorage.removeItem(KEPT_PROMPT);
    return kept?.trim() || null;
  }

  /** The room changed: show the box, the workspace, or neither. */
  render(state: StudioState): void {
    if (!this.info) return;
    const focus = state.focus;
    this.dock.hidden = focus.kind !== "room";
    this.tab5.show(focus.kind === "make");
    if (focus.kind === "make" && focus.app !== null) this.tab5.open(focus.app);
  }

  // ----------------------------------------------------------------- credits

  private get credits(): CreditsInfo | null {
    return this.info?.credits ?? null;
  }

  private signedIn(): boolean {
    return this.credits !== null;
  }

  private showCredits(): void {
    const credits = this.credits;
    this.dockCredits.textContent = credits ? t("make.credits", { n: credits.balance }) : t("make.dock.free", { n: this.info?.prices.trial ?? CREDITS.trial });
  }

  private setCredits(credits: CreditsInfo): void {
    if (!this.info) return;
    this.info = { ...this.info, credits };
    this.showCredits();
  }

  // -------------------------------------------------------------- the dock

  /** The account's newest apps, each a button that opens it. */
  private async showMine(): Promise<void> {
    const games = (await tab5Games().catch(() => ({ games: [] }))).games.slice(0, 4);
    if (games.length === 0) return;
    this.mine.replaceChildren(h("span", { class: "make-mine-label", text: t("make.tab5.mine") }),
      ...games.map((game) => h("button", { class: "make-way", type: "button", onclick: () => this.host.core()?.openMake(game.id) },
        game.versions.at(-1)?.title ?? t("make.tab5.new"))));
    this.mine.hidden = false;
  }

  private fromDock(): void {
    const text = this.dockField.value.trim();
    if (!text) {
      this.dockField.focus();
      return;
    }
    track("make.asked", { props: { from: "room" } });
    this.dockField.value = "";
    this.dock.classList.remove("typed");
    void this.begin(text);
  }

  /**
   * Starts an app from its first message: the workspace opens at once and
   * the agent starts. A visitor who is not signed in is asked to, and the
   * message waits in the tab for when they are.
   */
  async begin(text: string): Promise<void> {
    if (!this.signedIn()) {
      sessionStorage.setItem(KEPT_PROMPT, text.slice(0, MESSAGE_MAX));
      this.host.signIn(text);
      return;
    }
    const core = this.host.core();
    if (!core) return;
    core.openMake(null);
    await this.tab5.begin(text);
  }
}
